// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { useCallback, useEffect, useState } from 'react';
import { subscriptionsApi } from '@advisor-app/store/api/engine/subscriptions-api';
import { selectExpirationDate, userSlice } from '@shared/data-access/slices/user';
import { useAppDispatch, useAppSelector } from '@advisor-app/store/store';
import { environment } from '@shared/environment';

export const useAppSubscriptions = () => {
  const isActivatedSubscriptions = environment.FEATURES.SUBSCRIPTIONS_ENABLED;

  const dispatch = useAppDispatch();
  const expiration_date = useAppSelector(selectExpirationDate);

  const [canUserUseApp, setCanUserUseApp] = useState<boolean>(true);

  const [getSubscription] = subscriptionsApi.useLazyGetSubscriptionQuery();
  const [activateTrial] = subscriptionsApi.useLazyActivateTrialQuery();

  const addTrialPeriod = useCallback(async () => {
    /* istanbul ignore next */
    try {
      // add trial time
      const data = await activateTrial().unwrap();

      if (data.expiration_date) {
        dispatch(userSlice.actions.setUserData({ ...data }));
      }
    } catch (error) {
      console.error('[addTrialPeriod]', error);
    }
  }, [dispatch, activateTrial]);

  const checkExpirationDate = useCallback(async () => {
    if (expiration_date) return;
    /* istanbul ignore next */
    try {
      const data = await getSubscription().unwrap();

      if (!data.expiration_date) addTrialPeriod();

      dispatch(userSlice.actions.setUserData({ ...data }));
    } catch (error) {
      console.error('[checkExpirationDate]', error);
    }
  }, [addTrialPeriod, dispatch, getSubscription, expiration_date]);

  useEffect(() => {
    checkExpirationDate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (expiration_date && isActivatedSubscriptions) {
      const currentTimestamp = Date.now();
      setCanUserUseApp(expiration_date > currentTimestamp);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expiration_date]);

  return { expiration_date, canUserUseApp };
};
